import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { cn } from '../../lib/cn';
import { IconButton } from './IconButton';

type Size = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: Size;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  className?: string;
  'data-testid'?: string;
}

const sizes: Record<Size, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl',
};

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEsc = true,
  className,
  ...rest
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  // Ref para o onClose mais recente — evita que o efeito abaixo re-rode a cada
  // render (o que roubaria o foco do input a cada tecla digitada).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (closeOnEsc && e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // Intencional: depende só de isOpen/closeOnEsc (foco aplicado uma vez ao abrir)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, closeOnEsc]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center p-4 animate-fade-in"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm dark:bg-black/60" aria-hidden />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative z-10 flex w-full flex-col overflow-hidden rounded-2xl border border-border',
          'bg-surface-1 shadow-lg outline-none animate-scale-in max-h-[90vh]',
          sizes[size],
          className
        )}
        {...rest}
      >
        {(title || description) && (
          <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
            <div className="min-w-0">
              {title && <h2 className="font-display text-lg font-semibold text-foreground">{title}</h2>}
              {description && <p className="mt-0.5 text-sm text-foreground-muted">{description}</p>}
            </div>
            <IconButton aria-label="Fechar" icon={<CloseIcon />} onClick={onClose} className="-mr-2 -mt-1 shrink-0" />
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};
